import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, X as XIcon, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";

/**
 * Compact, dismissible "Postmoro was updated" banner shown inside the
 * authenticated shell when the current user has not seen the latest
 * release yet. Never a blocking modal, never repeated within a session
 * after dismiss.
 */
export default function UpdateBanner() {
  const { user } = useAuth();
  const { bannerVisible, latestRelease, dismissBanner } = useReleaseNotes();

  if (!user) return null;
  if (!bannerVisible || !latestRelease) return null;

  return (
    <div
      className="mx-4 sm:mx-6 lg:mx-10 mt-4"
      data-testid="update-banner"
      role="status"
    >
      <div className="max-w-[1320px] mx-auto flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.08)] px-4 py-2.5 text-sm">
        <span
          className="inline-flex items-center gap-1.5 text-[color:var(--brand-primary)] font-semibold shrink-0"
          aria-hidden="true"
        >
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="text-[color:var(--text-1)] min-w-0 flex-1 sm:flex-none">
          Postmoro was updated.
        </span>
        <span className="text-[color:var(--text-3)] text-xs hidden sm:inline">
          {latestRelease.date}
        </span>
        <button
          type="button"
          onClick={dismissBanner}
          aria-label="Dismiss update banner"
          data-testid="update-banner-dismiss"
          className="order-2 sm:order-3 inline-flex items-center justify-center h-7 w-7 rounded-md text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)] transition-colors shrink-0"
        >
          <XIcon className="h-4 w-4" />
        </button>
        <Link
          to="/whats-new"
          className="order-3 sm:order-2 w-full sm:w-auto sm:ml-auto inline-flex items-center justify-center gap-1 rounded-md bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] px-3 py-1.5 text-xs font-semibold transition-colors"
          data-testid="update-banner-cta"
        >
          See what&apos;s new <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
